import { isClient } from "@pharaday/utils";
import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const useIsOverflow = (
  elementRef: RefObject<HTMLDivElement | null>,
  callback?: (isOverflow: boolean) => void,
  // Depend on your use case, please use "horizontal" or "vertical" for more precision on the overflow detection, otherwise use "both"
  dimension: "horizontal" | "vertical" | "both" = "horizontal"
) => {
  const [isOverflow, setIsOverflow] = useState<boolean>(false);
  const callbackRef = useRef(callback);
  const dimensionRef = useRef(dimension);

  // Update refs when props change
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    dimensionRef.current = dimension;
  }, [dimension]);

  const checkOverflow = useCallback(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    let hasOverflow;
    const overflowVertical = element.scrollHeight > element.clientHeight;
    const overflowHorizontal = element.scrollWidth > element.clientWidth;

    switch (dimensionRef.current) {
      case "vertical":
        hasOverflow = overflowVertical;
        break;
      case "horizontal":
        hasOverflow = overflowHorizontal;
        break;
      case "both":
      default:
        hasOverflow = overflowVertical || overflowHorizontal;
        break;
    }

    setIsOverflow(hasOverflow);
    if (callbackRef.current) {
      callbackRef.current(hasOverflow);
    }
  }, [elementRef]);

  useLayoutEffect(() => {
    checkOverflow();
  }, [checkOverflow]);

  useEffect(() => {
    if (!isClient || !elementRef.current) {
      return;
    }

    const element = elementRef.current;
    let timeoutId: NodeJS.Timeout;

    // Debounced resize handler
    const debouncedCheckOverflow = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkOverflow, 16); // ~60fps
    };

    // Window resize listener
    window.addEventListener("resize", debouncedCheckOverflow);

    // ResizeObserver for element size changes
    const resizeObserver = new ResizeObserver(debouncedCheckOverflow);
    resizeObserver.observe(element);

    // MutationObserver for content changes
    const mutationObserver = new MutationObserver(debouncedCheckOverflow);
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", debouncedCheckOverflow);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [checkOverflow]);

  return isOverflow;
};

export default useIsOverflow;
